/**
 * "Forgot PIN" for someone with no verified phone (ADR 0041, `resetPinWithNewPhone`): confirm it is them
 * (login password, plus the authenticator code when two-factor is on), add a mobile number, prove it by SMS,
 * then choose a new PIN. One call at the end does all of it, so the password is only checked there — a wrong
 * one comes back to the first step. The authenticator code is asked for on that LAST step, beside "Save new
 * PIN": a code lasts 30 to 90 seconds, and one typed before the SMS round trip would be stale by the submit
 * (and every stale code is a strike against both lockouts).
 *
 * The step-up is what stops someone at an unlocked session from verifying their OWN number and unlocking
 * everyone's pay. A provider-only account has no step-up that would stop them, so it is pointed at an
 * administrator instead. The password and code are dropped from state the moment they are sent; no
 * password, code, proof or PIN is ever logged or stored.
 */

import { useState, type FormEvent } from 'react';
import { Banner, Button, Input } from '@hrms/ui';
import type { SalaryPinStatus } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { PasswordToggle } from '../../routes/auth/AuthLayout.js';
import { buildE164, DEFAULT_DIAL_CODE, mobileProblem } from '../../routes/auth/signupPhone.js';
import { maskedPinPhone, SalaryPinPhoneCode } from './SalaryPinPhoneCode.js';
import { localUnlockEnd } from './salaryPinGate.js';
import { NewPinFields, newPinProblem, PinActions } from './SalaryPinNewPinFields.js';
import { errorReason, salaryPinErrorMessage, salaryPinRequiredDetails } from './salaryPinErrors.js';

type Step = 'confirm' | 'phone' | 'code' | 'pin';

function statusOf(error: unknown): number | null {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : null;
}

function StepHeading({ children }: { children: string }) {
  return <p className="text-sm font-semibold text-fg">{children}</p>;
}

export function SalaryPinNewPhoneFlow({
  reset,
  onUnlocked,
  onLocked,
  onBack,
}: {
  reset: SalaryPinStatus['reset'];
  onUnlocked: (unlockedUntil: string) => void;
  onLocked?: (lockedUntil: string | null) => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState<Step>('confirm');
  const [password, setPassword] = useState('');
  const [passwordShown, setPasswordShown] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [passwordProblem, setPasswordProblem] = useState<string | null>(null);
  const [totpProblem, setTotpProblem] = useState<string | null>(null);
  const [typedPhone, setTypedPhone] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneProblem, setPhoneProblem] = useState<string | null>(null);
  const [proof, setProof] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pinProblem, setPinProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // The status can predate two-factor being turned on: once the server asks for a code, ask for it from then on.
  const [totpAsked, setTotpAsked] = useState(false);
  const needsTotp = reset.needsTotp || totpAsked;
  const resetPin = tsr.salaryPin.resetPinWithNewPhone.useMutation();

  if (reset.confirmWith === null || !reset.phoneVerification) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          {reset.confirmWith === null
            ? 'Your account signs in with Google or Microsoft. Ask your administrator to reset your salary PIN.'
            : 'Resetting your PIN needs a verified mobile number, and none is available here. Ask your administrator for help.'}
        </p>
        <PinActions>
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        </PinActions>
      </div>
    );
  }

  /** Where "Continue" on the first step goes: the first piece still missing (after a refused submit, often the PIN). */
  function afterConfirm(): Step {
    if (!phone) return 'phone';
    if (!proof) return 'code';
    return 'pin';
  }

  function submitConfirm(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!password) return setPasswordProblem('Enter your password');
    setPasswordProblem(null);
    setStep(afterConfirm());
  }

  function submitPhone(event: FormEvent) {
    event.preventDefault();
    const e164 = buildE164(DEFAULT_DIAL_CODE, typedPhone);
    const issue = e164 ? mobileProblem(e164) : 'Enter your mobile number';
    if (issue) return setPhoneProblem(issue);
    setPhoneProblem(null);
    setProblem(null);
    // A new number needs its own SMS proof.
    if (e164 !== phone) setProof(null);
    setPhone(e164);
    setStep('code');
  }

  function submitPin(event: FormEvent) {
    event.preventDefault();
    const issue = newPinProblem(pin, confirm);
    const noCode =
      needsTotp && !/^\d{6}$/.test(totpCode)
        ? 'Enter the 6-digit code from your authenticator app'
        : null;
    setPinProblem(issue);
    setTotpProblem(noCode);
    if (issue || noCode) return;
    setProblem(null);
    // Kept in state only until sent: a refused submit asks for it again.
    if (!password) {
      setProblem('Enter your password again to finish.');
      return setStep('confirm');
    }
    const body = {
      password,
      ...(needsTotp ? { totpCode } : {}),
      phone: phone!,
      phoneProof: proof!,
      newPin: pin,
    };
    setPassword('');
    setTotpCode('');
    resetPin.mutate(
      { body },
      {
        onSuccess: (result) => {
          if (result.status === 200) onUnlocked(localUnlockEnd(result.body.unlockedForMs));
        },
        onError: (error) => {
          const reason = errorReason(error);
          const status = statusOf(error);
          if (reason === 'password_incorrect') {
            setPasswordProblem('Wrong password');
            return setStep('confirm');
          }
          if (reason === 'totp_required' || reason === 'totp_incorrect') {
            // The password was right, but it was dropped once sent: ask for it again, then a fresh code on
            // the last step (where the error below shows).
            setTotpAsked(true);
            setTotpProblem(
              reason === 'totp_required'
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'That code is not right. Enter the current code from your authenticator app.',
            );
            setProblem('Enter your password again, then the current code.');
            return setStep('confirm');
          }
          if (reason === 'phone_unverified') {
            // The proof is spent or does not match the number: text a fresh code.
            setProof(null);
            setProblem(salaryPinErrorMessage(error, 'Verify your mobile number again.'));
            return setStep('code');
          }
          const refusal = salaryPinRequiredDetails(error);
          if (refusal?.reason === 'locked' && onLocked)
            return onLocked(refusal.lockedUntil ?? null);
          if (status === 409) {
            // The number is taken (or one is already on file): another number, another proof.
            setProof(null);
            setPhoneProblem(
              salaryPinErrorMessage(error, 'That number cannot be used. Try another one.'),
            );
            return setStep('phone');
          }
          if (status === 429) return setProblem('Too many attempts, try again later.');
          if (status === 422) return setProblem('Could not reset your PIN. Please try again.');
          setProblem(salaryPinErrorMessage(error, 'Could not reset your PIN. Please try again.'));
        },
      },
    );
  }

  const banner = problem && <Banner tone="danger">{problem}</Banner>;

  if (step === 'confirm') {
    return (
      <form className="flex flex-col gap-4" onSubmit={submitConfirm} noValidate>
        <StepHeading>Confirm it&apos;s you</StepHeading>
        <p className="text-sm text-fg-muted">
          You have no verified mobile number yet. Enter your password, then add your number to reset
          your PIN.
        </p>
        {banner}
        <Input
          label="Password"
          name="password"
          type={passwordShown ? 'text' : 'password'}
          autoComplete="current-password"
          suffix={
            <PasswordToggle shown={passwordShown} onToggle={() => setPasswordShown((v) => !v)} />
          }
          className="pr-11"
          value={password}
          error={passwordProblem ?? undefined}
          autoFocus
          onChange={(event) => {
            setPassword(event.target.value);
            setPasswordProblem(null);
          }}
        />
        <PinActions>
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" variant="primary">
            Continue
          </Button>
        </PinActions>
      </form>
    );
  }

  if (step === 'phone') {
    return (
      <form className="flex flex-col gap-4" onSubmit={submitPhone} noValidate>
        <StepHeading>Add your mobile number</StepHeading>
        <p className="text-sm text-fg-muted">
          We will text it a code. Once verified it becomes your number for resetting your PIN and
          password.
        </p>
        <Input
          label="Mobile number"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+91 98765 43210"
          hint="Include the country code. Numbers without one are taken as India (+91)."
          value={typedPhone}
          error={phoneProblem ?? undefined}
          autoFocus
          onChange={(event) => {
            setTypedPhone(event.target.value);
            setPhoneProblem(null);
          }}
        />
        <PinActions>
          <Button type="button" variant="ghost" onClick={() => setStep('confirm')}>
            Back
          </Button>
          <Button type="submit" variant="primary">
            Send code
          </Button>
        </PinActions>
      </form>
    );
  }

  if (step === 'code' || !proof || !phone) {
    return (
      <div className="flex flex-col gap-4">
        {phone && (
          <SalaryPinPhoneCode
            // A new number starts a new verification (and a new SMS).
            key={phone}
            config={reset.phoneVerification}
            phone={phone}
            onVerified={(value) => {
              setProblem(null);
              setProof(value);
              setStep('pin');
            }}
          />
        )}
        {banner}
        <PinActions>
          <Button type="button" variant="ghost" onClick={() => setStep('phone')}>
            Change number
          </Button>
        </PinActions>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submitPin} noValidate>
      <p className="text-sm text-fg-muted">
        Verified <span className="font-semibold text-fg">{maskedPinPhone(phone)}</span>. Choose a
        new salary PIN.
      </p>
      {banner}
      <NewPinFields
        pin={pin}
        confirm={confirm}
        onPin={(value) => {
          setPin(value);
          setPinProblem(null);
        }}
        onConfirm={(value) => {
          setConfirm(value);
          setPinProblem(null);
        }}
        problem={pinProblem}
        disabled={resetPin.isPending}
      />
      {needsTotp && (
        <Input
          label="Authenticator code"
          name="totpCode"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="000000"
          hint="The code showing in your authenticator app right now."
          className="text-center font-mono text-lg tracking-[0.5em]"
          value={totpCode}
          disabled={resetPin.isPending}
          error={totpProblem ?? undefined}
          onChange={(event) => {
            setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
            setTotpProblem(null);
          }}
        />
      )}
      <PinActions>
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary" loading={resetPin.isPending}>
          Save new PIN
        </Button>
      </PinActions>
    </form>
  );
}
